import { captureException } from '@sentry/react';
import { Component, ErrorInfo, ReactNode } from 'react';

import { ANALYTICS_EVENTS } from '@/config/analytics/events';
import posthog from '@/config/analytics/posthog';

interface FeatureErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  name?: string;
  onReset?: () => void;
}

interface FeatureErrorBoundaryState {
  error: Error | null;
  hasError: boolean;
}

/**
 * Lightweight, reusable error boundary for wrapping individual feature sections
 * within a page.
 *
 * Unlike the route-level error boundaries (RouteErrorBoundary,
 * RouterAwareErrorBoundary), this component renders a compact inline fallback
 * so users can retry the failed section without leaving or reloading the page.
 *
 * @example
 * <FeatureErrorBoundary name="Chat Messages">
 *   <ChatMessages />
 * </FeatureErrorBoundary>
 */
class FeatureErrorBoundary extends Component<FeatureErrorBoundaryProps, FeatureErrorBoundaryState> {
  public override state: FeatureErrorBoundaryState = {
    error: null,
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): FeatureErrorBoundaryState {
    return { error, hasError: true };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(
      `[FeatureErrorBoundary${this.props.name ? `: ${this.props.name}` : ''}]`,
      error,
      errorInfo,
    );

    captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
      ...(this.props.name ? { tags: { feature: this.props.name } } : {}),
    });

    posthog.capture(ANALYTICS_EVENTS.ERROR_BOUNDARY_TRIGGERED, {
      boundary: 'feature',
      error: error.message,
      ...(this.props.name ? { feature: this.props.name } : {}),
    });
  }

  private handleRetry = () => {
    this.setState({ error: null, hasError: false });
    this.props.onReset?.();
  };

  public override render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback !== undefined) {
      return this.props.fallback;
    }

    const title = this.props.name
      ? `Failed to load ${this.props.name}`
      : 'Failed to load this section';

    return (
      <div className="border-border-subtle bg-back-bg2 flex flex-col items-center justify-center gap-3 rounded-lg border p-6 text-center">
        <span aria-label="Warning" className="text-2xl" role="img">
          ⚠️
        </span>
        <p className="text-secondary text-sm font-medium">{title}</p>
        <button
          className="bg-elevated-hover text-secondary hover:bg-border-strong rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
          onClick={this.handleRetry}
          type="button"
        >
          Try again
        </button>
      </div>
    );
  }
}

export { FeatureErrorBoundary };
export default FeatureErrorBoundary;
